import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Pimg } from '../lib/ui.jsx';
import { supabase, rpc } from '../lib/supabase.js';
import { B, todayISO, addD, thDate, fmtSize } from '../lib/util.js';
import { calcContract, expandPicked, groupLines } from '../lib/calc.js';
import { ContractSheet, TermsSheet } from '../lib/print.jsx';

const EMPTY_FORM = {
  entity: 'person', id: null, first_name: '', last_name: '', company: '', tax_id: '',
  tax_branch: '', contact: '', phone: '', email: '', line_id: '', address: '',
};

export default function NewRental({ go }) {
  const { can, tiers, volumeDiscounts, vatRate, whtRate, settings, me } = useAuth();
  const { catalog, customers, freeUnitsRange, nextFree, unitsOfSku, refresh } = useData();
  const { toast, openSheet, closeSheet } = useUI();

  const [form, setForm] = useState(EMPTY_FORM);
  const [tierKey, setTierKey] = useState('new');
  const [fromDate, setFromDate] = useState(todayISO());
  const [nights, setNights] = useState(2);
  const [idType, setIdType] = useState('citizen_id');
  const [files, setFiles] = useState([]);        // { file, url, name, size, mime }
  const [signed, setSigned] = useState(false);
  const [picked, setPicked] = useState(() => new Map());   // sku → จำนวนชิ้นที่เช่า
  const [pickCat, setPickCat] = useState('ทั้งหมด');
  const [search, setSearch] = useState('');
  const [onlyFree, setOnlyFree] = useState(false);
  const [inf, setInf] = useState({ posts: 3, days: 14, platform: 'IG + TikTok', tag: '#CampStudioTH' });
  const [depMethod, setDepMethod] = useState('cash');
  const [busy, setBusy] = useState(false);
  const fileIn = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  const tier = tiers.find(t => t.key === tierKey) || tiers[0] || { key: 'new', name: '', dep_pct: .5, rent_mul: 1 };
  const toDate = addD(fromDate, Math.max(1, +nights || 2));
  const isCo = form.entity === 'company';

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // เลือกลูกค้าเดิม → เติมฟอร์ม
  const pickCustomer = (id) => {
    if (!id) { setForm(EMPTY_FORM); return; }
    const c = customers.find(x => x.id === id);
    if (!c) return;
    setForm({ ...EMPTY_FORM, ...c });
    if (c.tier) setTierKey(c.tier);
  };

  const custOk = isCo
    ? (form.company.trim() && /^\d{13}$/.test(form.tax_id.trim()) && form.phone.trim())
    : (form.first_name.trim() && form.last_name.trim() && form.phone.trim());

  // ---------- อุปกรณ์ ----------
  const cats = useMemo(() => ['ทั้งหมด', ...new Set(catalog.map(c => c.category))], [catalog]);

  // คิดความว่างของทุก SKU ครั้งเดียวต่อการเปลี่ยนช่วงวัน — ไม่ใช่ทุกครั้งที่พิมพ์ตัวอักษรในฟอร์ม
  // (130 SKU × คิววันว่าง 90 วัน = งานหนักพอที่จะทำให้ช่องกรอกชื่อกระตุก)
  const nightsN = Math.max(1, +nights || 2);
  const availMap = useMemo(() => {
    const m = new Map();
    for (const c of catalog) m.set(c.sku, freeUnitsRange(c.sku, fromDate, toDate));
    return m;
  }, [catalog, freeUnitsRange, fromDate, toDate]);
  const soonestMap = useMemo(() => {
    const m = new Map();
    for (const c of catalog) {
      if ((availMap.get(c.sku) || []).length > 0) continue;   // ว่างอยู่แล้ว ไม่ต้องหาคิว
      let best = null;
      for (const u of unitsOfSku(c.sku)) {
        if (u.status === 'retired') continue;
        const f = nextFree(u.id, nightsN);
        if (f && (!best || f < best)) best = f;
      }
      m.set(c.sku, best);
    }
    return m;
  }, [catalog, availMap, unitsOfSku, nextFree, nightsN]);

  const list = useMemo(() => {
    const q = search.toLowerCase().trim();
    return catalog.filter(c => {
      if (pickCat !== 'ทั้งหมด' && c.category !== pickCat) return false;
      if (q && !(c.name + ' ' + c.sku).toLowerCase().includes(q)) return false;
      if (onlyFree && (availMap.get(c.sku) || []).length === 0) return false;
      return true;
    });
  }, [catalog, pickCat, search, onlyFree, availMap]);

  // เปลี่ยนวันที่แล้วของว่างลดลง — หั่นจำนวนที่เลือกไว้ลงให้พอดีของว่าง แล้วบอกผู้ใช้ว่าปรับให้
  // dep แค่ availMap: ตอนกดเลือกมีเพดานอยู่แล้ว ไม่ต้องวนซ้ำทุกครั้งที่ picked เปลี่ยน
  useEffect(() => {
    let cut = false;
    const next = new Map();
    for (const [sku, q] of picked) {
      const n = Math.min(q, (availMap.get(sku) || []).length);
      if (n !== q) cut = true;
      if (n > 0) next.set(sku, n);
    }
    if (cut) { setPicked(next); toast('ปรับจำนวนที่เลือกลงตามของว่างในช่วงวันใหม่'); }
  }, [availMap]);   // eslint-disable-line react-hooks/exhaustive-deps

  const freeOf = (sku) => (availMap.get(sku) || []).length;
  const setQty = (sku, n) => {
    const q = Math.max(0, Math.min(n, freeOf(sku)));
    setPicked(p => {
      const m = new Map(p);
      if (q > 0) m.set(sku, q); else m.delete(sku);
      return m;
    });
  };
  const bump = (sku, d) => setQty(sku, (picked.get(sku) || 0) + d);
  // กดที่แถว = เพิ่มทีละชิ้น · เต็มของว่างแล้วบอกไปตรงๆ ว่าเหลือเท่าไร
  const rowClick = (sku) => {
    const free = freeOf(sku);
    if (free === 0) return;
    const cur = picked.get(sku) || 0;
    if (cur >= free) { toast(`ช่วงวันที่เลือกไว้ รุ่นนี้ว่าง ${free} ชิ้น เพิ่มได้เท่านี้`); return; }
    setQty(sku, cur + 1);
  };

  // 1 แถวต่อ 1 ชิ้น (พร้อม unit ที่จะจอง) — ตัวคำนวณและ DB คิดต่อชิ้นเหมือนกัน
  const pickedItems = useMemo(() => expandPicked(picked, catalog, availMap), [picked, catalog, availMap]);
  const totalQty = pickedItems.length;
  const calc = calcContract({
    items: pickedItems, nights: +nights, tier, entity: form.entity,
    vatRate, whtRate, settings, volumeDiscounts,
  });
  const sumLines = useMemo(() => groupLines(calc.lines), [calc.lines]);

  // ---------- เอกสาร ----------
  const addFiles = (fl) => {
    const next = [...fl].filter(f => f.size <= 10 * 1048576).map(f => ({
      file: f, name: f.name, size: f.size, mime: f.type,
      url: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    if (next.length < fl.length) toast('ข้ามไฟล์ที่เกิน 10 MB');
    setFiles(x => [...x, ...next]);
  };
  const delFile = (i) => setFiles(x => { if (x[i]?.url) URL.revokeObjectURL(x[i].url); return x.filter((_, j) => j !== i); });

  // ---------- checklist ----------
  const missing = [];
  if (!custOk) missing.push(isCo ? 'ข้อมูลนิติบุคคล: ชื่อบริษัท เลขภาษี 13 หลัก และเบอร์ติดต่อ' : 'ข้อมูลผู้เช่า: ชื่อ นามสกุล และเบอร์ติดต่อ');
  if (!files.length) missing.push('แนบเอกสารยืนยันตัวตนอย่างน้อย 1 ไฟล์');
  if (!signed) missing.push('ติ๊กยืนยันว่าลูกค้าลงนามข้อตกลงการเช่าแล้ว');
  if (!totalQty) missing.push('เลือกอุปกรณ์อย่างน้อย 1 ชิ้น');
  if (tierKey === 'inf' && (!inf.posts || !inf.days)) missing.push('กรอกข้อตกลงคอนเทนต์ให้ครบ');
  const canOpen = !missing.length && !busy && navigator.onLine;

  const steps = [
    ['01 ผู้เช่า', custOk],
    ['02 ช่วงเวลา', !!fromDate && +nights >= 1],
    ['03 เอกสาร', files.length > 0 && signed],
    ['04 อุปกรณ์', totalQty > 0],
  ];

  // ---------- เปิดสัญญา ----------
  const openContract = async () => {
    if (!canOpen) return;
    setBusy(true);
    try {
      // 1) อัปโหลดเอกสารเข้า private bucket
      const idDocs = [];
      for (const f of files) {
        const path = `${todayISO().slice(0, 7)}/${crypto.randomUUID()}-${f.name.replace(/[^\w.\-ก-๙]/g, '_')}`;
        const { error } = await supabase.storage.from('rent-id-docs').upload(path, f.file, { upsert: false });
        if (error) throw new Error('อัปโหลดเอกสารไม่สำเร็จ: ' + error.message);
        idDocs.push({ path, name: f.name, size: f.size, mime: f.mime, doc_type: idType });
      }
      // 2) เปิดสัญญา (DB คิดเงิน + กันจองซ้อน + audit + คิว SaleCore)
      const unitIds = pickedItems.map(c => ({ unit_id: c.unit_id }));
      const res = await rpc('rp_open_contract', {
        p: {
          customer: { ...form, tier: tierKey },
          tier: tierKey, entity: form.entity,
          from_date: fromDate, nights: Math.max(1, +nights || 2),
          items: unitIds, deposit_method: depMethod,
          inf_deliverable: tierKey === 'inf' ? inf : null,
          id_docs: idDocs,
        },
      });
      toast(`เปิดสัญญา ${res.contract_no} แล้ว — ลงปฏิทินและพิมพ์ใบเช่าให้ลูกค้าเซ็น`);
      // 3) พิมพ์ใบเช่า — calc.lines พก unit_code ของชิ้นที่จองจริงมาแล้วจาก pickedItems
      openSheet(<ContractSheet no={res.contract_no} customer={form} entity={form.entity}
        tier={tier} nights={calc.nights} fromDate={fromDate} toDate={toDate}
        calc={calc} idDocCount={files.length}
        idType={idType === 'citizen_id' ? 'บัตรประชาชน' : 'หนังสือเดินทาง'}
        branchName={me.branch_name || 'สาขาหลัก'} />);
      // 4) ล้างฟอร์ม
      files.forEach(f => f.url && URL.revokeObjectURL(f.url));
      setFiles([]); setSigned(false); setPicked(new Map()); setForm(EMPTY_FORM);
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  const preview = () => {
    if (!totalQty) { toast('เลือกอุปกรณ์ก่อนดูตัวอย่าง'); return; }
    openSheet(<ContractSheet no="(ตัวอย่าง — ยังไม่เปิดสัญญา)" customer={form} entity={form.entity}
      tier={tier} nights={calc.nights} fromDate={fromDate} toDate={toDate}
      calc={calc} idDocCount={files.length}
      idType={idType === 'citizen_id' ? 'บัตรประชาชน' : 'หนังสือเดินทาง'}
      branchName={me.branch_name || 'สาขาหลัก'} />);
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: '1.6fr 1fr', alignItems: 'start' }}>
      <div>
        <div className="steps">
          {steps.map(([label, done], i) => (
            <span key={i} className={done ? 'done' : (i === steps.findIndex(s => !s[1]) ? 'on' : '')}>
              <b>{label.split(' ')[0]}</b> {label.split(' ').slice(1).join(' ')}
            </span>
          ))}
        </div>
        <h2>เปิดสัญญาเช่าใหม่</h2>
        <div className="hint">กรอกตามลำดับ 4 ขั้น — ระบบคำนวณมัดจำ ภาษี และเช็ควันว่างให้อัตโนมัติ</div>

        <label className="fld">1 · ข้อมูลผู้เช่า</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="seg">
            <button className={!isCo ? 'on' : ''} onClick={() => set('entity', 'person')}>บุคคลธรรมดา</button>
            <button className={isCo ? 'on' : ''} onClick={() => set('entity', 'company')}>นิติบุคคล</button>
          </div>
          <select style={{ flex: 1, minWidth: 200 }} value={form.id || ''} onChange={e => pickCustomer(e.target.value)}>
            <option value="">ลูกค้าใหม่ — กรอกข้อมูลด้านล่าง</option>
            {customers.filter(c => c.entity === form.entity).map(c => (
              <option key={c.id} value={c.id}>
                {c.entity === 'company' ? c.company : `${c.first_name || ''} ${c.last_name || ''}`} · {c.phone || '—'}
              </option>
            ))}
          </select>
        </div>

        {!isCo ? (
          <div className="formgrid">
            <div><label className="mini">ชื่อ</label><input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="ชื่อจริง" /></div>
            <div><label className="mini">นามสกุล</label><input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="นามสกุล" /></div>
          </div>
        ) : (
          <div className="formgrid">
            <div className="span2"><label className="mini">ชื่อนิติบุคคล</label><input value={form.company} onChange={e => set('company', e.target.value)} placeholder="บริษัท ... จำกัด" /></div>
            <div><label className="mini">เลขประจำตัวผู้เสียภาษี (13 หลัก)</label><input className="mono-in" maxLength={13} value={form.tax_id} onChange={e => set('tax_id', e.target.value.replace(/\D/g, ''))} placeholder="0000000000000" /></div>
            <div><label className="mini">สำนักงาน / สาขา</label><input value={form.tax_branch} onChange={e => set('tax_branch', e.target.value)} placeholder="สำนักงานใหญ่" /></div>
            <div className="span2"><label className="mini">ผู้ติดต่อ</label><input value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="ชื่อผู้ประสานงาน" /></div>
          </div>
        )}
        <div className="formgrid">
          <div><label className="mini">เบอร์ติดต่อ</label><input className="mono-in" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="08x-xxx-xxxx" /></div>
          <div><label className="mini">อีเมล</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="name@mail.com" /></div>
          <div className="span2"><label className="mini">LINE ID</label><input value={form.line_id} onChange={e => set('line_id', e.target.value)} placeholder="@lineid" /></div>
          <div className="span2"><label className="mini">ที่อยู่</label><textarea rows={2} value={form.address} onChange={e => set('address', e.target.value)} placeholder="บ้านเลขที่ ถนน ตำบล อำเภอ จังหวัด รหัสไปรษณีย์" /></div>
        </div>

        <label className="fld">2 · ระดับลูกค้า</label>
        <div className={'catfilter' + (can('rate.special') ? '' : ' nope')}>
          {tiers.map(t => (
            <button key={t.key} className={t.key === tierKey ? 'on' : ''} onClick={() => setTierKey(t.key)}>{t.name}</button>
          ))}
        </div>
        <div className="hint" style={{ marginTop: -10 }}>{tier.note}</div>
        {!can('rate.special') && <div className="hint" style={{ marginTop: -6 }}>ระดับพิเศษต้องให้ผู้มีสิทธิ์ "อนุมัติเรทพิเศษ" เป็นคนเลือก</div>}

        {tierKey === 'inf' && (
          <div>
            <label className="fld">ข้อตกลงคอนเทนต์ (Influencer)</label>
            <div className="formgrid">
              <div><label className="mini">จำนวนโพสต์</label><input className="mono-in" type="number" min={1} value={inf.posts} onChange={e => setInf({ ...inf, posts: +e.target.value })} /></div>
              <div><label className="mini">ส่งภายใน (วัน)</label><input className="mono-in" type="number" min={1} value={inf.days} onChange={e => setInf({ ...inf, days: +e.target.value })} /></div>
              <div><label className="mini">แพลตฟอร์ม</label><input value={inf.platform} onChange={e => setInf({ ...inf, platform: e.target.value })} /></div>
              <div><label className="mini">แท็กที่ต้องใส่</label><input value={inf.tag} onChange={e => setInf({ ...inf, tag: e.target.value })} /></div>
            </div>
            <div className="note show" style={{ marginTop: 10 }}>ค่าเช่าได้รับยกเว้นแลกกับคอนเทนต์ตามเงื่อนไขนี้ ต้องแนบ "ข้อตกลงคอนเทนต์" ท้ายสัญญาและให้ลงนาม · หากไม่ส่งงานครบภายในกำหนด ผู้ให้เช่ามีสิทธิเรียกเก็บค่าเช่าตามอัตราปกติย้อนหลัง</div>
          </div>
        )}

        <label className="fld">2 · ช่วงเวลา</label>
        <div className="formgrid" style={{ maxWidth: 420 }}>
          <div><label className="mini">วันรับอุปกรณ์</label><input type="date" className="mono-in" value={fromDate} onChange={e => setFromDate(e.target.value || todayISO())} /></div>
          <div><label className="mini">จำนวนคืน (ฐาน 2 คืน 3 วัน)</label><input type="number" className="mono-in" min={1} max={30} value={nights} onChange={e => setNights(e.target.value)} /></div>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          รับ {thDate(fromDate)} → คืน {thDate(toDate)}
          {+nights > 2 && <> · คืนที่เกินฐานคิดเพิ่มคืนละ 35% ของเรททริป</>}
        </div>

        <label className="fld">3 · เอกสารยืนยันตัวตน (บังคับทุกสัญญา)</label>
        <div className="seg">
          <button className={idType === 'citizen_id' ? 'on' : ''} onClick={() => setIdType('citizen_id')}>บัตรประชาชน</button>
          <button className={idType === 'passport' ? 'on' : ''} onClick={() => setIdType('passport')}>หนังสือเดินทาง</button>
        </div>
        <div className="dropzone"
          onClick={() => fileIn.current?.click()}
          onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('over'); }}
          onDragLeave={e => e.currentTarget.classList.remove('over')}
          onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('over'); addFiles(e.dataTransfer.files); }}>
          <input ref={fileIn} type="file" accept="image/*,application/pdf" multiple hidden
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <div className="dz-icon">↑</div>
          <div className="dz-main">ลากไฟล์มาวาง หรือเลือกไฟล์</div>
          <div className="dz-sub">JPG, PNG หรือ PDF — ถ่ายจากมือถือได้ ไม่เกิน 10 MB ต่อไฟล์</div>
        </div>
        <div>
          {files.map((f, i) => (
            <div className="frow" key={i}>
              <div className="fthumb" onClick={() => f.url && setLightbox(f)}>
                {f.url ? <><img src={f.url} alt="" /><span className="eye">กดดู</span></> : <span className="pdfmark">PDF</span>}
              </div>
              <div className="fmeta"><div className="fn">{f.name}</div><div className="fs">{fmtSize(f.size)}</div></div>
              <button className="fdel" onClick={() => delFile(i)}>ลบ</button>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 13.5 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={signed} onChange={e => setSigned(e.target.checked)} /> ลูกค้าอ่านและลงนามข้อตกลงการเช่าแล้ว
          </label>
          <button className="linkbtn" onClick={() => openSheet(<TermsSheet />)}>อ่านข้อตกลงการเช่าฉบับเต็ม →</button>
        </div>
        <div className="pdpa">
          <b>PDPA</b> ไฟล์เอกสารเก็บใน bucket ส่วนตัว · เปิดดูได้เฉพาะผู้มีสิทธิ์และมีบันทึกการเข้าถึงทุกครั้ง · ภาพเบลอไว้เป็นค่าเริ่มต้น กดค้างเพื่อดู · ลบอัตโนมัติ {Number(settings.id_purge_days ?? 90)} วันหลังปิดสัญญา
        </div>

        <label className="fld">4 · เลือกอุปกรณ์</label>
        <div className="hint" style={{ marginTop: -2, marginBottom: 4 }}>
          กดที่แถวสินค้าเพื่อเพิ่มทีละชิ้น หรือกดปุ่ม − + ปรับจำนวนทางขวา · เพิ่มได้ไม่เกินจำนวนที่ว่างในช่วงวันที่เลือกไว้
        </div>
        <div className="pickbar">
          <input className="picksearch" placeholder="ค้นหาชื่อสินค้า หรือ SKU…" value={search} onChange={e => setSearch(e.target.value)} />
          <label className="chk"><input type="checkbox" checked={onlyFree} onChange={e => setOnlyFree(e.target.checked)} /> ซ่อนที่ไม่ว่าง</label>
        </div>
        <div className="catfilter">
          {cats.map(c => <button key={c} className={c === pickCat ? 'on' : ''} onClick={() => setPickCat(c)}>{c}</button>)}
        </div>
        {totalQty > 0 && (
          <div className="pickcount">เลือกแล้ว <b>{totalQty}</b> ชิ้น จาก <b>{picked.size}</b> รุ่น
            <button className="clr" onClick={() => setPicked(new Map())}>ล้างทั้งหมด</button></div>
        )}
        <div>
          {list.map(c => {
            const free = (availMap.get(c.sku) || []).length;
            const q = picked.get(c.sku) || 0;
            const next = free === 0 ? soonestMap.get(c.sku) : null;
            return (
              <div key={c.sku} className={'item-row' + (q > 0 ? ' sel' : '') + (free === 0 ? ' off' : '')} onClick={() => rowClick(c.sku)}>
                <div className="pthumb"><Pimg cat={c.category} size={40} image={c.image_url} /></div>
                <div>
                  <div className="nm">{c.name}</div>
                  <div className="sku">{c.sku} · {c.category} · {free > 0 ? `ว่าง ${free} ชิ้น` : (next ? `คิวว่างถัดไป ${thDate(next)}` : 'ไม่ว่างช่วงนี้')}</div>
                </div>
                <div className="px">
                  <div className="rent">{B(c.trip_rate)}/ทริป</div>
                  <div className="dep">มัดจำ {B(Math.min(Math.round(c.retail * Number(tier.dep_pct)), Number(settings.deposit_cap ?? 10000)))}/ชิ้น</div>
                </div>
                {free > 0 && (
                  <div className="qty" onClick={e => e.stopPropagation()}>
                    <div className="ql">จำนวน</div>
                    <div className="qs">
                      <button type="button" className="qb" disabled={q === 0}
                        title="ลดจำนวน" onClick={() => bump(c.sku, -1)}>−</button>
                      <span className="qn">{q}</span>
                      <button type="button" className="qb" disabled={q >= free}
                        title={q >= free ? `ว่างแค่ ${free} ชิ้น` : 'เพิ่มจำนวน'} onClick={() => bump(c.sku, 1)}>+</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!list.length && <div className="empty">ไม่พบสินค้า</div>}
        </div>
      </div>

      <div className="card calc">
        <h2>สรุปสัญญา</h2>
        <div>
          {!calc.lines.length ? <div className="empty">ยังไม่ได้เลือกอุปกรณ์</div> : (<>
            {sumLines.map(l => (
              <div className="row" key={l.sku}><span>{l.name}{l.qty > 1 ? <b style={{ fontFamily: 'var(--mono)' }}> × {l.qty}</b> : ''}</span>
                <span className="a">{Number(tier.rent_mul) === 0 ? '—' : B(l.rent)}</span></div>
            ))}
            <div className="row"><span>ค่าเช่ารวม ({totalQty} ชิ้น · {calc.nights} คืน · {tier.name}{Number(tier.rent_mul) !== 1 ? ` · ×${tier.rent_mul}` : ''})</span>
              <span className="a">{Number(tier.rent_mul) === 0 ? 'ยกเว้น' : B(calc.gross)}</span></div>
            {calc.volAmt > 0 && <div className="row"><span>ส่วนลดปริมาณ {calc.vol.label} (−{Math.round(calc.volOff * 100)}%)</span>
              <span className="a" style={{ color: 'var(--gold)' }}>−{B(calc.volAmt)}</span></div>}
            {calc.net > 0 && (<>
              <div className="row"><span>มูลค่าก่อน VAT</span><span className="a">{B(calc.exVat)}</span></div>
              <div className="row"><span>VAT {Math.round(vatRate * 100)}%</span><span className="a">{B(calc.vat)}</span></div>
            </>)}
            {calc.wht > 0 && <div className="row"><span>หัก ณ ที่จ่าย {Math.round(whtRate * 100)}% (นิติบุคคล)</span>
              <span className="a" style={{ color: 'var(--gold)' }}>−{B(calc.wht)}</span></div>}
            <div className="row"><span>มัดจำ ({Math.round(Number(tier.dep_pct) * 100)}%{calc.capped ? ' · ติดเพดาน' : ''})</span><span className="a">{B(calc.dep)}</span></div>
            <div className="row total"><span>รับเงินวันนี้</span><span className="a">{B(calc.cash)} ฿</span></div>
            {calc.wht > 0 && <div className="hint" style={{ marginTop: 8 }}>ต้องขอหนังสือรับรองการหักภาษี ณ ที่จ่ายจากลูกค้า · มัดจำไม่ถูกหัก ณ ที่จ่าย เพราะไม่ใช่รายได้</div>}
            {tier.key === 'inf' && <div className="hint" style={{ marginTop: 8 }}>ยกเว้นค่าเช่า แต่ยังเก็บมัดจำ {B(calc.dep)} ฿ · ต้องแนบข้อตกลงคอนเทนต์</div>}
          </>)}
        </div>
        <div className={'note' + (calc.capped ? ' show' : '')}>มัดจำเกินเพดาน {B(Number(settings.deposit_cap ?? 10000))} ต่อชิ้น — แนะนำใช้ pre-auth บัตรเครดิตแทนการโอนจริง เพื่อลด friction</div>
        {calc.dep > 0 && (
          <div style={{ marginTop: 12 }}>
            <label className="mini" style={{ display: 'block', marginBottom: 4 }}>วิธีรับเงินประกัน</label>
            <div className="catfilter" style={{ marginBottom: 0 }}>
              {[['cash', 'เงินสด'], ['transfer', 'โอนเข้าบัญชี'], ['preauth', 'กันวงเงินบัตร']].map(([k, n]) => (
                <button key={k} className={depMethod === k ? 'on' : ''} onClick={() => setDepMethod(k)}>{n}</button>
              ))}
            </div>
          </div>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <button className="btn" disabled={!canOpen} onClick={openContract}>{busy ? 'กำลังบันทึก…' : 'เปิดสัญญา + พิมพ์ใบเช่า'}</button>
          <button className="btn ghost" onClick={preview}>ดูตัวอย่างใบเช่า</button>
        </div>
        {missing.length ? (
          <div className="todo">
            <div className="t">ยังขาดอีก {missing.length} อย่าง</div>
            {missing.map((m, i) => <div className="i" key={i}><span className="b">○</span><span>{m}</span></div>)}
          </div>
        ) : <div className="ready">✓ ครบทุกขั้นตอน — เปิดสัญญาได้เลย</div>}
        <div className="hint" style={{ marginTop: 10 }}>เปิดสัญญาแล้วระบบจะลงคิวในปฏิทินให้อัตโนมัติ และบันทึกภาพสภาพอุปกรณ์ทุกชิ้นก่อนส่งมอบ</div>
      </div>

      {lightbox && (
        <div className="lightbox" onClick={e => { if (e.target.classList.contains('lightbox')) setLightbox(null); }}>
          <div className="lb-bar"><span>{lightbox.name}</span>
            <button className="lb-close" onClick={() => setLightbox(null)}>ปิด (ESC)</button></div>
          <img src={lightbox.url} alt="" />
        </div>
      )}
    </div>
  );
}
